import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MESSAGE_TYPE, useFloatMessage } from '../../Providers/FloatMessage';
import {
  SubmitSuggestion as BLSubmitSuggestion,
  GetPublicConfig as BLGetPublicConfig
} from '../../../utilities/BL/Admin/Overlays/Playlist';
import {
  ConnectWebSocketForPlaylistAdmin,
  DisconnectWebSocketForPlaylistAdmin
} from '../../../utilities/WS/Admin/PlaylistWS';

// ==============================================================================
// CONFIGURACIÓN Y CONSTANTES LOCALES
// ==============================================================================
const SESSION_STORAGE_KEY = 'playlist_fan_session_id';
const COOLDOWN_STORAGE_KEY = 'playlist_next_allowed_ts';
const DEFAULT_COOLDOWN_SECONDS = 120;

/**
 * Obtiene o inicializa un identificador único de sesión de aficionado para persistir en localStorage.
 * @returns {string} GUID o identificador aleatorio de sesión.
 */
function getOrCreateFanSessionId() {
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      sessionId = crypto.randomUUID();
    } else {
      sessionId = 'fan_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    }
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    } catch (e) {
      console.warn('[PlaylistFan] No se pudo persistir fanSessionId en localStorage:', e);
    }
  }
  return sessionId;
}

/**
 * Formatea milisegundos a formato textual 'm:ss'.
 * @param {number} ms 
 * @returns {string} Formato 'm:ss'
 */
function msToMinSec(ms) {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Componente de Experiencia Web Móvil para el Aficionado: Arma la Playlist [HU-08]
 * Permite sugerir canciones o artistas en tiempo real, validando cooldowns, origen y estado de dinámica.
 * 
 * @param {Object} props
 * @param {string} [props.roomId] - Identificador de sala provisto por RoomLoader o router.
 */
const PlaylistFan = ({ roomId: propRoomId }) => {
  const [searchParams] = useSearchParams();
  const urlRoomId = searchParams.get('roomId');
  const effectiveRoomId = propRoomId || urlRoomId || '';
  const urlSource = searchParams.get('s') || searchParams.get('source') || 'mobile_web';

  const { showMessage } = useFloatMessage();
  const [fanSessionId] = useState(getOrCreateFanSessionId);

  // --- VISTAS: 'intro' | 'song_form' | 'artist_form' | 'sent' | 'paused' ---
  const [view, setView] = useState('intro');

  // --- FORMULARIO ---
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    artistName: '',
    fanName: ''
  });
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // --- CONFIGURACIÓN REMOTA ---
  const [isSubmissionActive, setIsSubmissionActive] = useState(true);
  const [submissionPausedMessage, setSubmissionPausedMessage] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(DEFAULT_COOLDOWN_SECONDS);

  // --- COOLDOWN ---
  const [nextAllowedTs, setNextAllowedTs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // 1. Cargar cooldown persistido al montar
  useEffect(() => {
    const saved = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (saved) {
      const ts = parseInt(saved, 10);
      if (!Number.isNaN(ts)) {
        setNextAllowedTs(ts);
      }
    }
  }, []);

  // 2. Contador regresivo del cooldown
  useEffect(() => {
    const tick = () => {
      if (!nextAllowedTs) {
        setRemainingMs(0);
        return;
      }
      const diff = nextAllowedTs - Date.now();
      setRemainingMs(Math.max(0, diff));
    };

    tick();
    const intervalId = setInterval(tick, 500);
    return () => clearInterval(intervalId);
  }, [nextAllowedTs]);

  const canStartNew = !nextAllowedTs || Date.now() >= nextAllowedTs;

  // 3. Consulta inicial de configuración pública y escucha WebSocket / SignalR en vivo
  useEffect(() => {
    let isMounted = true;

    const fetchConfig = async () => {
      try {
        const response = await BLGetPublicConfig({ roomId: effectiveRoomId });
        if (!isMounted) return;

        if (response && response.success && response.data) {
          const cfg = response.data;
          if (cfg.isSubmissionActive !== undefined) {
            setIsSubmissionActive(cfg.isSubmissionActive);
            if (cfg.isSubmissionActive === false) {
              setView('paused');
            }
          }
          if (cfg.submissionPausedMessage) {
            setSubmissionPausedMessage(cfg.submissionPausedMessage);
          }
          if (cfg.cooldownSeconds) {
            setCooldownSeconds(cfg.cooldownSeconds);
          }
        }
      } catch (err) {
        console.error('[PlaylistFan] Error al consultar configuración pública:', err);
      }
    };

    fetchConfig();

    const handleIncomingMessage = (message) => {
      if (!isMounted || !message) return;
      const type = (message.type || '').toLowerCase();
      const payload = message.payload || message.data || {};

      if (type === 'settings_updated' || type === 'playlist:settings_updated') {
        if (payload.isSubmissionActive !== undefined) {
          setIsSubmissionActive(payload.isSubmissionActive);
          if (payload.isSubmissionActive === false) {
            setView('paused');
          } else {
            setView((prev) => (prev === 'paused' ? 'intro' : prev));
          }
        }
        if (payload.submissionPausedMessage !== undefined) {
          setSubmissionPausedMessage(payload.submissionPausedMessage);
        }
        if (payload.cooldownSeconds !== undefined) {
          setCooldownSeconds(payload.cooldownSeconds);
        }
      } else if (type === 'dynamic_reset' || type === 'playlist:dynamic_reset') {
        fetchConfig();
      }
    };

    ConnectWebSocketForPlaylistAdmin(handleIncomingMessage, () => {}, effectiveRoomId);

    return () => {
      isMounted = false;
      DisconnectWebSocketForPlaylistAdmin();
    };
  }, [effectiveRoomId]);

  // --- HANDLERS DE FORMULARIO ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'title' && value.length > 100) return;
    if (name === 'artist' && value.length > 80) return;
    if (name === 'artistName' && value.length > 100) return;
    if (name === 'fanName' && value.length > 50) return;

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg(null);
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();

    if (!canStartNew) {
      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: `Espera ${msToMinSec(remainingMs)} para enviar otra sugerencia.`
      });
      return;
    }

    if (!isSubmissionActive) {
      setView('paused');
      return;
    }

    const isSong = type === 'song';
    const suggestionTitle = isSong ? formData.title.trim() : formData.artistName.trim();
    const suggestionArtist = isSong ? formData.artist.trim() : formData.artistName.trim();

    if (!suggestionTitle) {
      setErrorMsg(isSong ? 'Escribe el título de la canción.' : 'Escribe el nombre del artista o banda.');
      return;
    }

    setIsSending(true);
    setErrorMsg(null);

    try {
      const payload = {
        roomId: effectiveRoomId,
        suggestionType: isSong ? 0 : 1, // 0: Canción, 1: Artista
        title: suggestionTitle,
        artistName: suggestionArtist,
        fanName: formData.fanName.trim(),
        fanSessionId: fanSessionId,
        clientIpAddress: urlSource
      };

      const response = await BLSubmitSuggestion(payload);

      if (response && response.success) {
        // Activar Cooldown
        const currentCooldownMs = (cooldownSeconds || DEFAULT_COOLDOWN_SECONDS) * 1000;
        const ts = Date.now() + currentCooldownMs;
        setNextAllowedTs(ts);
        try {
          localStorage.setItem(COOLDOWN_STORAGE_KEY, String(ts));
        } catch (_) {}

        // Limpiar formulario y pasar a confirmación
        setFormData({ title: '', artist: '', artistName: '', fanName: '' });
        setView('sent');
        showMessage({
          type: MESSAGE_TYPE.GOOD,
          text: '¡Tu sugerencia fue enviada a la cabina con éxito!'
        });
      } else {
        const msg = response?.message || 'No se pudo enviar la sugerencia. Intenta de nuevo.';
        setErrorMsg(msg);
        showMessage({ type: MESSAGE_TYPE.BAD, text: msg });
      }
    } catch (err) {
      console.error('[PlaylistFan] Error al enviar sugerencia:', err);
      const msg = 'Ocurrió un error de conexión inesperado.';
      setErrorMsg(msg);
      showMessage({ type: MESSAGE_TYPE.BAD, text: msg });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="container SelfieFan Messages__Fan Playlist__Fan">
      {/* VISTA 1: INTRO / BIENVENIDA */}
      {view === 'intro' && (
        <div className="SelfieFan__Intro container pt-4">
          <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="SelfieFan__Fan__Title">¡Bienvenid@ Aficionad@!</p>
          <p className="SelfieFan__Fan__Text--Center">¡Armemos la playlist de nuestro equipo!</p>

          <ol className="SelfieFan__List">
            <li>Elige si deseas sugerir una Canción o un Artista</li>
            <li>Escribe los datos y tu nombre</li>
            <li>Da clic en “Enviar a la playlist”</li>
          </ol>

          <p className="Playlist__Fan__Text--Center">
            Tu sugerencia podría sonar en las bocinas del estadio durante el partido
          </p>

          <div className="Playlist__Fan__SelectionActions">
            {!canStartNew ? (
              <button
                className="Btn__Adsl--Red Playlist__Fan__SelectionBtn"
                disabled
                title={`Puedes sugerir otra en ${msToMinSec(remainingMs)}`}
              >
                <i className="bi bi-clock-history me-2"></i> Puedes sugerir otra en {msToMinSec(remainingMs)}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="Btn__Adsl--Red Playlist__Fan__SelectionBtn"
                  onClick={() => {
                    setErrorMsg(null);
                    setView('song_form');
                  }}
                >
                  <i className="bi bi-music-note-beamed me-2"></i> Sugerir Canción
                </button>
                <button
                  type="button"
                  className="Btn__Adsl--RedOutline Playlist__Fan__SelectionBtn"
                  onClick={() => {
                    setErrorMsg(null);
                    setView('artist_form');
                  }}
                >
                  <i className="bi bi-mic-fill me-2"></i> Sugerir Artista
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* VISTA 2A: FORMULARIO DE CANCIÓN */}
      {view === 'song_form' && (
        <div className="Messages__Fan__FormContainer pt-4">
          <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="Messages__Fan__Text--Title Messages__Fan__Text--Center Messages__Fan__Element--SpaceUp">
            <i className="bi bi-music-note-beamed text-danger me-2"></i>Sugerir Canción
          </p>

          <form className="Messages__Fan__FormContainer__Form" onSubmit={(e) => handleSubmit(e, 'song')}>
            <label className="Messages__Fan__FormContainer__Form--SpaceUp">
              Título de la canción <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ej. El Sonidito / Matador / Despacito"
              maxLength={100}
              disabled={isSending}
              autoFocus
            />
            <div className="text-end small">{formData.title.length}/100</div>

            <label className="Messages__Fan__FormContainer__Form--SpaceUp">Cantante o banda (opcional)</label>
            <input
              type="text"
              className="form-control"
              name="artist"
              value={formData.artist}
              onChange={handleChange}
              placeholder="Ej. Los Fabulosos Cadillacs / Banda MS"
              maxLength={80}
              disabled={isSending}
            />
            <div className="text-end small">{formData.artist.length}/80</div>

            <label className="Messages__Fan__FormContainer__Form--SpaceUp">¿Quién la pide? (opcional)</label>
            <input
              type="text"
              className="form-control"
              name="fanName"
              value={formData.fanName}
              onChange={handleChange}
              placeholder="Ej. Carlos / Familia Pérez / Zona 1"
              maxLength={50}
              disabled={isSending}
            />
            <div className="text-end small">{formData.fanName.length}/50</div>

            <button
              type="submit"
              className="Btn__Adsl--Red Messages__Fan__FormContainer__Form--SpaceUp"
              disabled={isSending || !formData.title.trim()}
            >
              {isSending ? (
                'Enviando sugerencia...'
              ) : (
                <>
                  <i className="bi bi-music-note-beamed me-2"></i> Enviar a la playlist
                </>
              )}
            </button>
            <button
              type="button"
              className="Btn__Adsl--RedOutline Messages__Fan__FormContainer__Form--SpaceUp"
              onClick={() => setView('intro')}
              disabled={isSending}
            >
              Volver
            </button>

            {errorMsg && <p className="Messages__Fan__Text--Center text-danger mt-3 fw-bold">{errorMsg}</p>}
          </form>
        </div>
      )}

      {/* VISTA 2B: FORMULARIO DE ARTISTA */}
      {view === 'artist_form' && (
        <div className="Messages__Fan__FormContainer pt-4">
          <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="Messages__Fan__Text--Title Messages__Fan__Text--Center Messages__Fan__Element--SpaceUp">
            <i className="bi bi-mic-fill text-danger me-2"></i>Sugerir Artista
          </p>

          <form className="Messages__Fan__FormContainer__Form" onSubmit={(e) => handleSubmit(e, 'artist')}>
            <label className="Messages__Fan__FormContainer__Form--SpaceUp">
              Artista o banda <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="artistName"
              value={formData.artistName}
              onChange={handleChange}
              placeholder="Ej. Bad Bunny / Queen / Banda MS / Dua Lipa"
              maxLength={100}
              disabled={isSending}
              autoFocus
            />
            <div className="text-end small">{formData.artistName.length}/100</div>

            <label className="Messages__Fan__FormContainer__Form--SpaceUp">¿Quién lo pide? (opcional)</label>
            <input
              type="text"
              className="form-control"
              name="fanName"
              value={formData.fanName}
              onChange={handleChange}
              placeholder="Ej. Carlos / Familia Pérez / Zona 1"
              maxLength={50}
              disabled={isSending}
            />
            <div className="text-end small">{formData.fanName.length}/50</div>

            <button
              type="submit"
              className="Btn__Adsl--Red Messages__Fan__FormContainer__Form--SpaceUp"
              disabled={isSending || !formData.artistName.trim()}
            >
              {isSending ? (
                'Enviando sugerencia...'
              ) : (
                <>
                  <i className="bi bi-mic-fill me-2"></i> Enviar a la playlist
                </>
              )}
            </button>
            <button
              type="button"
              className="Btn__Adsl--RedOutline Messages__Fan__FormContainer__Form--SpaceUp"
              onClick={() => setView('intro')}
              disabled={isSending}
            >
              Volver
            </button>

            {errorMsg && <p className="Messages__Fan__Text--Center text-danger mt-3 fw-bold">{errorMsg}</p>}
          </form>
        </div>
      )}

      {/* VISTA 3: CONFIRMACIÓN Y COOLDOWN */}
      {view === 'sent' && (
        <div className="SelfieFan__Sent container pt-4">
          <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="fs-1 mb-0 text-success">
            <i className="bi bi-check-circle-fill"></i>
          </p>
          <p className="SelfieFan__Sent__Title">¡Petición enviada!</p>
          <p className="Playlist__Fan__Text--Center">
            Tu sugerencia ha sido enviada a la cabina de sonido. ¡Podría sonar en el estadio!
          </p>

          <div className="SelfieFan__Sent__Actions justify-content-center">
            <button
              className="Btn__Adsl--RedOutline"
              onClick={() => canStartNew && setView('intro')}
              disabled={!canStartNew}
            >
              {canStartNew ? (
                <>
                  <i className="bi bi-arrow-repeat me-2"></i> Enviar otra sugerencia
                </>
              ) : (
                `Puedes enviar otra en ${msToMinSec(remainingMs)}`
              )}
            </button>
          </div>

          <hr />

          <a
            href="https://interaktia.com/?utm_source=playlist_web&utm_medium=banner&utm_campaign=fdm_xolos_s2"
            target="_blank"
            rel="noreferrer"
            className="SelfieFan__Sent__Banner"
          >
            <img
              src="/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif"
              alt="Publicidad"
              className="d-block m-auto SelfieFan__Sent__Banner__Image"
            />
          </a>
          <a
            href="https://interaktia.com/?utm_source=playlist_web&utm_medium=banner&utm_campaign=fdm_xolos_s2"
            target="_blank"
            rel="noreferrer"
            className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block"
          >
            Contactar ahora
          </a>

          <hr />

          <p className="text-center mb-0">¿Quieres conocer más de esta tecnología?</p>
          <p className="text-center mb-0">Interaktia está listo para ti</p>
          <a
            href="https://interaktia.com/?utm_source=playlist_web&utm_medium=banner_text&utm_campaign=fdm_xolos_s2"
            target="_blank"
            rel="noreferrer"
            className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center"
          >
            Contactar ahora
          </a>
        </div>
      )}

      {/* VISTA 4: DINÁMICA PAUSADA / CERRADA */}
      {view === 'paused' && (
        <div className="SelfieFan__Sent container pt-4">
          <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
          <p className="fs-1 mb-0 text-warning">
            <i className="bi bi-pause-circle-fill"></i>
          </p>
          <p className="SelfieFan__Sent__Title text-warning">Dinámica Pausada</p>
          <p className="Playlist__Fan__Text--Center fs-5">
            {submissionPausedMessage ||
              '¡Gracias por participar! La playlist para este partido ha cerrado. ¡Disfruta el encuentro!'}
          </p>

          <hr />

          <a
            href="https://interaktia.com/?utm_source=playlist_web&utm_medium=banner&utm_campaign=fdm_xolos_s2"
            target="_blank"
            rel="noreferrer"
            className="SelfieFan__Sent__Banner"
          >
            <img
              src="/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif"
              alt="Publicidad"
              className="d-block m-auto SelfieFan__Sent__Banner__Image"
            />
          </a>
          <a
            href="https://interaktia.com/?utm_source=playlist_web&utm_medium=banner&utm_campaign=fdm_xolos_s2"
            target="_blank"
            rel="noreferrer"
            className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block"
          >
            Contactar ahora
          </a>

          <hr />

          <p className="text-center mb-0">¿Quieres conocer más de esta tecnología?</p>
          <p className="text-center mb-0">Interaktia está listo para ti</p>
          <a
            href="https://interaktia.com/?utm_source=playlist_web&utm_medium=banner_text&utm_campaign=fdm_xolos_s2"
            target="_blank"
            rel="noreferrer"
            className="Btn__Adsl--RedOutline m-auto text-decoration-none text-center"
          >
            Contactar ahora
          </a>
        </div>
      )}

      <footer className="text-center mt-3 FooterCredits">
        Hecho con ♥ por{' '}
        <a
          href="https://interaktia.com/?utm_source=playlist_web&utm_medium=footer&utm_campaign=fdm_xolos_s2"
          target="_blank"
          rel="noreferrer"
          className="text-dark"
        >
          Interaktia
        </a>{' '}
        | 2026
      </footer>
    </div>
  );
};

export { PlaylistFan };
export default PlaylistFan;
